import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { scoreColor, RadialScore } from "@/components/ScoreMeter";
import OpportunityCard from "@/components/OpportunityCard";
import { useSaved } from "@/lib/useSaved";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  ArrowRight, Telescope, ShieldCheck, Hammer, Users, Radar, Sparkles,
  TrendingUp, Zap, Check, BarChart3, Target, PartyPopper,
} from "lucide-react";

const FAQ = [
  ["Is this just another AI tool directory?", "No. NexxVibe is an opportunity intelligence platform. We help you discover what's worth building, validate it with AI, plan the MVP, and go to market — a full workflow, not a list of links."],
  ["Where does the opportunity data come from?", "Opportunities are curated and scored across demand, competition, monetization and build difficulty. Every AI analysis is clearly tagged so you always know what's verified data versus an AI estimate."],
  ["Do I need to be technical?", "Not at all. Whether you're a no-code builder, indie hacker or experienced developer, NexxVibe tailors opportunities and build plans to your skills."],
  ["Is NexxVibe really 100% free?", "Yes — completely free, with no credit card required, ever. Every feature is unlocked for everyone: opportunity discovery, unlimited AI analysis, idea validation, build planning and the full community. No trials, no plans, no paywalls."],
];

const FEATURES = [
  { icon: Telescope, title: "Discover What's Worth Building", text: "Browse a scored feed of vetted software opportunities across every niche and business model." },
  { icon: ShieldCheck, title: "Validate Before You Build", text: "Get a rigorous AI validation report with demand, competition and monetization scores in seconds." },
  { icon: Hammer, title: "Turn Ideas Into Products", text: "Generate lean MVP build plans, tech stacks and roadmaps so you never over-scope." },
  { icon: Radar, title: "Intelligence For Modern Builders", text: "Track rising trends, map competitors, and model revenue with real unit economics." },
  { icon: Target, title: "Find Customers Faster", text: "Every opportunity comes with GTM playbooks and a first-10-customers strategy." },
  { icon: Users, title: "Built For A New Generation", text: "Join a community of builders shipping products and sharing what actually works." },
];

export default function Landing() {
  const { user } = useAuth();
  const { savedIds, toggleSave } = useSaved();
  const [opps, setOpps] = useState([]);

  useEffect(() => { api.get("/opportunities?limit=6&sort=overall").then((r) => setOpps(r.data)); }, []);

  const ticker = [...opps, ...opps];
  const ctaLink = user ? "/discover" : "/login";

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grid-bg">
        <div className="aurora" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-mono text-cyan-300 mb-6 fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />The Builder Intelligence Platform
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50 leading-[1.05] fade-up">
            Stop guessing<br /><span className="bg-gradient-to-r from-cyan-400 via-cyan-300 to-indigo-400 bg-clip-text text-transparent">what to build.</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mt-6 fade-up">
            Discover, validate, and build better software opportunities with AI-powered builder intelligence. The Bloomberg Terminal for software opportunities.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8 fade-up">
            <Link to={ctaLink}><Button className="h-12 px-7 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-base gap-2" data-testid="hero-cta-btn">Find My Next Opportunity <ArrowRight className="w-4 h-4" /></Button></Link>
            <Link to="/discover"><Button variant="outline" className="h-12 px-7 border-white/15 text-slate-200 text-base" data-testid="hero-explore-btn">Explore Opportunities</Button></Link>
          </div>
          <div className="flex items-center justify-center gap-6 mt-8 text-xs text-slate-500 font-mono fade-up">
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" />No credit card</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" />AI-powered analysis</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" />100% free forever</span>
          </div>

          <div className="flex justify-center mt-10 fade-up">
            <div className="free-ribbon relative overflow-hidden rounded-2xl px-6 py-4 sm:px-9 sm:py-5 shadow-2xl w-full max-w-2xl" data-testid="free-banner"
              style={{ boxShadow: "0 10px 50px -8px rgba(250,204,21,0.45)" }}>
              <span className="shine" aria-hidden="true" />
              <div className="relative z-10 flex items-center justify-center gap-3 sm:gap-4">
                <PartyPopper className="w-8 h-8 sm:w-10 sm:h-10 text-slate-900 shrink-0" strokeWidth={2.5} />
                <div className="text-center">
                  <p className="font-display font-extrabold text-slate-900 leading-tight text-xl sm:text-3xl tracking-tight" style={{ textShadow: "0 1px 0 rgba(255,255,255,0.35)" }}>
                    No Credit Card Needed!
                  </p>
                  <p className="font-display font-extrabold text-slate-900 leading-tight text-2xl sm:text-4xl tracking-tight">
                    This site is <span className="underline decoration-wavy decoration-white/70 underline-offset-4">100% FREE</span>
                  </p>
                </div>
                <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-slate-900 shrink-0" strokeWidth={2.5} />
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard preview */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="glass rounded-2xl p-4 sm:p-6 glow-cyan fade-up">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500/60" /><span className="w-3 h-3 rounded-full bg-amber-500/60" /><span className="w-3 h-3 rounded-full bg-emerald-500/60" /></div>
              <span className="text-xs font-mono text-slate-500 ml-2">nexxvibe — opportunity radar</span>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {opps.slice(0, 3).map((o) => (
                <Link key={o.slug} to={`/opportunities/${o.slug}`} className="rounded-xl bg-white/5 border border-white/8 p-4 hover:border-cyan-500/40 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-mono uppercase text-cyan-400">{o.category}</span>
                    <RadialScore value={o.overall_score} size={44} stroke={5} />
                  </div>
                  <p className="text-sm font-semibold text-slate-100 line-clamp-2">{o.title}</p>
                  <p className="text-[11px] font-mono text-emerald-400 mt-2">{o.revenue_potential}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ticker */}
      {opps.length > 0 && (
        <section className="border-y border-white/8 py-3 overflow-hidden bg-slate-950/50">
          <div className="flex gap-8 marquee w-max">
            {ticker.map((o, i) => (
              <span key={i} className="flex items-center gap-2 text-sm whitespace-nowrap">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-slate-300">{o.title}</span>
                <span className="font-mono font-bold" style={{ color: scoreColor(o.overall_score) }}>{o.overall_score}</span>
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-14">
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-3">The Workflow</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-50">From "I need an idea" to "I'm ready to launch"</h2>
          <p className="text-slate-400 mt-3 max-w-2xl mx-auto">Every feature moves you further along the journey — discovery, validation, build, launch, grow.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="glass rounded-xl p-6 card-hover">
              <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center mb-4"><f.icon className="w-5 h-5 text-cyan-400" /></div>
              <h3 className="font-display text-lg font-semibold text-slate-100 mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Live opportunities */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2">Live from the engine</p>
            <h2 className="font-display text-3xl font-bold text-slate-50">Top-scored opportunities right now</h2>
          </div>
          <Link to="/discover" className="hidden sm:inline-flex items-center gap-1.5 text-cyan-400 hover:gap-2.5 transition-all text-sm">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {opps.map((o) => <OpportunityCard key={o.slug} opp={o} saved={savedIds.has(o.slug)} onSave={toggleSave} />)}
        </div>
      </section>

      {/* Stats band */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="glass rounded-2xl p-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[[Telescope, "Scored Opportunities", "50+"], [BarChart3, "Data Points Per Opp", "12"], [Sparkles, "AI Agents", "6"], [Zap, "Minutes To Validate", "<1"]].map(([Icon, label, val]) => (
            <div key={label}><Icon className="w-6 h-6 text-cyan-400 mx-auto mb-2" /><p className="font-mono text-3xl font-extrabold text-slate-50">{val}</p><p className="text-xs text-slate-500 mt-1">{label}</p></div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-3xl font-bold text-slate-50 text-center mb-10">Questions, answered</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={i} value={`item-${i}`} className="glass rounded-xl px-5 border-white/8">
              <AccordionTrigger className="text-slate-200 hover:text-cyan-300 text-left">{q}</AccordionTrigger>
              <AccordionContent className="text-slate-400">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="glass rounded-3xl p-12 text-center relative overflow-hidden glow-cyan">
          <div className="aurora" />
          <div className="relative z-10">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50 leading-tight">Your next big product could be<br />hiding in plain sight.</h2>
            <p className="text-slate-300 mt-4 max-w-xl mx-auto">Join the builders using intelligence — not guesswork — to decide what to build next.</p>
            <Link to={ctaLink}><Button className="h-12 px-8 mt-8 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-base gap-2" data-testid="final-cta-btn">Find My Next Opportunity <ArrowRight className="w-4 h-4" /></Button></Link>
            <p className="text-sm font-semibold text-emerald-300 mt-4">100% free — no credit card, ever.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
